import { useState, KeyboardEvent } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  CircularProgress,
  Chip,
  Box,
} from "@mui/material";
import Iconify from "src/components/iconify";
import { useCountryData } from "src/components/competitive-intelligence/hooks/useCountryData";


// ----------------------------------------------------------------------

type AddBrandModalProps = {
  open: boolean;
  onClose: () => void;
  onAddBrand: (brand: { name: string; domains: string; countries: string }) => Promise<boolean>;
};

// ----------------------------------------------------------------------

export default function AddBrandModal({ open, onClose, onAddBrand }: AddBrandModalProps) {
  const [brandName, setBrandName] = useState("");
  const [domains, setDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState("");
  const [countries, setCountries] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { countriesData } = useCountryData();

  const handleCountryChange = (event: SelectChangeEvent) => {
    setCountries(event.target.value);
  };

  const addDomain = (domain: string) => {
    const trimmed = domain.trim().toLowerCase();
    if (trimmed && !domains.includes(trimmed)) {
      setDomains([...domains, trimmed]);
    }
    setDomainInput("");
  };

  const handleDomainKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (domainInput.trim()) {
        addDomain(domainInput);
      }
    } else if (event.key === "Backspace" && !domainInput && domains.length > 0) {
      // Remove last domain when backspace is pressed on empty input
      setDomains(domains.slice(0, -1));
    }
  };

  const handleRemoveDomain = (domainToRemove: string) => {
    setDomains(domains.filter((d) => d !== domainToRemove));
  };

  const handleSubmit = async () => {
    // Add any remaining input as a domain before submitting
    const finalDomains = domainInput.trim()
      ? [...domains, domainInput.trim().toLowerCase()]
      : domains;

    if (!brandName.trim() || finalDomains.length === 0) return;

    setIsSubmitting(true);
    try {
      const success = await onAddBrand({
        name: brandName.trim(),
        domains: finalDomains.join(", "),
        countries,
      });

      if (success) {
        // Reset form and close modal
        setBrandName("");
        setDomains([]);
        setDomainInput("");
        setCountries("");
        onClose();
      }
    } catch (error) {
      console.error("Error adding brand:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setBrandName("");
    setDomains([]);
    setDomainInput("");
    setCountries("");
    onClose();
  };

  const isFormValid = brandName.trim() && (domains.length > 0 || domainInput.trim());

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: 520,
          maxWidth: "100%",
          bgcolor: "#f3f6fb",
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: "rgba(22, 28, 36, 0.2)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
        }}
      >
        <span style={{ fontSize: 18, fontWeight: 500, color: "#333c47" }}>
          Add new brand
        </span>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          size="small"
          sx={{ p: 0, color: "#5e6d7b" }}
        >
          <Iconify icon="mingcute:close-line" width={16} />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3, pt: 1, overflow: "visible" }}>
        <Stack spacing={2}>
          {/* Brand name field */}
          <TextField
            label="Brand name"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            fullWidth
            disabled={isSubmitting}
            InputLabelProps={{
              sx: {
                fontSize: 12,
                fontWeight: 600,
                color: "#919eab",
                bgcolor: "#f3f6fb",
                px: 0.5,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                "& fieldset": {
                  borderColor: "rgba(145, 158, 171, 0.3)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(145, 158, 171, 0.5)",
                },
              },
            }}
          />

          {/* Domains field with chips */}
          <Box
            sx={{
              border: "1px solid rgba(145, 158, 171, 0.3)",
              borderRadius: 1,
              bgcolor: "#f3f6fb",
              minHeight: 56,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.75,
              p: 1.5,
              position: "relative",
              "&:hover": {
                borderColor: "rgba(145, 158, 171, 0.5)",
              },
              "&:focus-within": {
                borderColor: "#181818",
                borderWidth: 2,
                p: "11px", // Compensate for 2px border
              },
            }}
          >
            {/* Floating label */}
            <Box
              component="span"
              sx={{
                position: "absolute",
                top: -8,
                left: 10,
                fontSize: 12,
                fontWeight: 600,
                color: "#919eab",
                bgcolor: "#f3f6fb",
                px: 0.5,
              }}
            >
              Domains of the brand
            </Box>

            {domains.map((domain) => (
              <Chip
                key={domain}
                label={domain}
                size="small"
                onDelete={isSubmitting ? undefined : () => handleRemoveDomain(domain)}
                sx={{
                  marginTop: "7px",
                  bgcolor: "#f1f7b5",
                  color: "#333c47",
                  fontWeight: 700,
                  fontSize: 13,
                  "& .MuiChip-deleteIcon": {
                    color: "#919eab",
                    fontSize: 16,
                    "&:hover": {
                      color: "#333c47",
                    },
                  },
                }}
              />
            ))}

            <Box
              component="input"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={handleDomainKeyDown}
              disabled={isSubmitting}
              placeholder={domains.length === 0 ? "e.g. example.com, example.fr" : ""}
              sx={{
                flex: 1,
                minWidth: 120,
                border: "none",
                outline: "none",
                fontSize: 14,
                color: "#333c47",
                bgcolor: "transparent",
                "&::placeholder": {
                  color: "#919eab",
                },
                "&:disabled": {
                  color: "#919eab",
                },
              }}
            />
          </Box>

          {/* Countries dropdown */}
          <FormControl fullWidth disabled={isSubmitting}>
            <InputLabel
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#919eab",
                bgcolor: "#f3f6fb",
                px: 0.5,
              }}
            >
              Countries
            </InputLabel>
            <Select
              value={countries}
              onChange={handleCountryChange}
              label="Countries"
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: "#919eab" }}>Select a country</span>;
                }
                return selected;
              }}
              sx={{
                borderRadius: 1,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(145, 158, 171, 0.3)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(145, 158, 171, 0.5)",
                },
              }}
            >
              {countriesData.map((country) => (
                <MenuItem key={country.country_code} value={country.country_name}>
                  {country.country_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Submit button */}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={14} sx={{ color: "inherit" }} />
              ) : null
            }
            sx={{
              alignSelf: "flex-start",
              bgcolor: "#181818",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              textTransform: "none",
              "&:hover": {
                bgcolor: "#333",
              },
              "&.Mui-disabled": {
                bgcolor: "#181818",
                color: "#fff",
                opacity: 0.5,
              },
            }}
          >
            {isSubmitting ? "Adding..." : "Add brand"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
